// File Name: ServiceDetailPage.jsx

import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Cpu,
  Users,
  LineChart,
  HeartHandshake,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Globe,
  Layers3,
  Sparkles,
} from "lucide-react";

import { SERVICES } from "@/lib/site";

const ICONS = {
  Cpu,
  Users,
  LineChart,
  HeartHandshake,
};

/* SERVICE IMAGES */

const SERVICE_IMAGES = {
  "it-services":
    "https://images.unsplash.com/photo-1515879218367-8466d910aaa4?q=80&w=1400&auto=format&fit=crop",

  "staffing-solutions":
    "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=1400&auto=format&fit=crop",

  "business-consulting":
    "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1400&auto=format&fit=crop",

  "human-resources":
    "https://images.unsplash.com/photo-1573496799652-408c2ac9fe98?q=80&w=1400&auto=format&fit=crop",
};

export default function ServiceDetailPage() {
  const { slug } = useParams();

  const s = SERVICES.find((x) => x.slug === slug);

  if (!s) {
    return <Navigate to="/services" replace />;
  }

  const Icon = ICONS[s.icon] || Cpu;

  const serviceImage =
    SERVICE_IMAGES[slug] ||
    "https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=1400&auto=format&fit=crop";

  return (
    <>
      {/* HERO SECTION */}

      <section className="relative overflow-hidden bg-gradient-to-br from-white via-violet-50 to-fuchsia-50 pt-28 md:pt-32 lg:pt-36 pb-16 md:pb-24">

        {/* GLOW EFFECTS */}

        <div className="absolute top-0 right-0 w-72 h-72 md:w-[450px] md:h-[450px] bg-violet-300/30 rounded-full blur-3xl" />

        <div className="absolute bottom-0 left-0 w-64 h-64 md:w-[350px] md:h-[350px] bg-fuchsia-200/40 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8">

          {/* BACK BUTTON */}

          <Link
            to="/services"
            className="inline-flex items-center text-sm text-violet-600 hover:text-violet-700 mb-8 font-medium transition"
          >
            ← Back to services
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* LEFT CONTENT */}

            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="order-2 lg:order-1"
            >

              {/* ICON */}

              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-violet-100 border border-violet-200 flex items-center justify-center mb-6 shadow-sm">

                <Icon className="w-7 h-7 md:w-8 md:h-8 text-violet-600" />

              </div>

              {/* LABEL */}

              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-violet-200 bg-violet-50 text-violet-700 text-xs sm:text-sm mb-5 font-medium">

                <Sparkles className="w-4 h-4" />

                Enterprise Grade Solutions

              </div>

              {/* TITLE */}

              <h1
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight text-slate-900"
                style={{ fontFamily: "Sora" }}
              >
                {s.title}
              </h1>

              {/* SHORT */}

              <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed">
                {s.short}
              </p>

              {/* DESCRIPTION */}

              <p className="mt-5 text-sm sm:text-base text-slate-500 leading-relaxed">
                At CodeFix Solutions, we provide tailored technology and business
                solutions that help organizations modernize operations, improve
                efficiency and accelerate growth through innovative digital
                transformation strategies.
              </p>

              {/* TAGS */}

              <div className="flex flex-wrap gap-3 mt-8">

                {[
                  "Enterprise Solutions",
                  "Cloud Native",
                  "Secure Infrastructure",
                  "Scalable Architecture",
                  "Global Delivery",
                ].map((item) => (
                  <div
                    key={item}
                    className="px-4 py-2 rounded-full border border-violet-200 bg-white text-xs sm:text-sm text-violet-700 shadow-sm"
                  >
                    {item}
                  </div>
                ))}

              </div>

              {/* BUTTONS */}

              <div className="flex flex-col sm:flex-row gap-4 mt-10">

                <Link
                  to="/contact?type=consultation"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-medium transition hover:scale-[1.02] shadow-purple"
                >
                  Book Consultation

                  <ArrowRight className="w-4 h-4" />
                </Link>

                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl border border-violet-200 bg-white text-slate-700 hover:bg-violet-50 transition shadow-sm"
                >
                  Contact Us
                </Link>

              </div>

            </motion.div>

            {/* IMAGE */}

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="relative order-1 lg:order-2"
            >

              <div className="absolute inset-0 bg-violet-300/20 rounded-[36px] blur-3xl" />

              <div className="relative rounded-[28px] md:rounded-[36px] overflow-hidden border border-violet-100 shadow-purple-lg">

                <img
                  src={serviceImage}
                  alt={s.title}
                  className="w-full h-[300px] sm:h-[420px] md:h-[520px] object-cover"
                />

              </div>

            </motion.div>

          </div>

        </div>

      </section>

      {/* FEATURES */}

      <section className="py-16 md:py-24 bg-gradient-to-b from-violet-50/40 to-white">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">

          {/* TOP CARDS */}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-16">

            {[
              {
                icon: ShieldCheck,
                title: "Secure & Reliable",
                desc:
                  "Industry-standard security practices and enterprise infrastructure ensuring maximum protection and reliability.",
              },

              {
                icon: Globe,
                title: "Global Expertise",
                desc:
                  "Experienced professionals delivering world-class digital solutions for enterprises and modern businesses.",
              },

              {
                icon: Layers3,
                title: "Scalable Solutions",
                desc:
                  "Future-ready scalable architectures designed for long-term growth and seamless expansion.",
              },
            ].map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-[28px] border border-violet-100 bg-white p-6 md:p-8 shadow-sm hover:shadow-xl transition"
              >

                {React.createElement(card.icon, {
                  className: "w-10 h-10 text-violet-600 mb-5",
                })}

                <h3
                  className="text-xl md:text-2xl font-bold text-slate-900 mb-4"
                  style={{ fontFamily: "Sora" }}
                >
                  {card.title}
                </h3>

                <p className="text-sm md:text-base text-slate-600 leading-relaxed">
                  {card.desc}
                </p>

              </motion.div>
            ))}

          </div>

          {/* INCLUDED */}

          <div className="mb-10">

            <div className="inline-flex items-center gap-2 text-violet-600 mb-4">

              <Sparkles className="w-4 h-4" />

              <span className="uppercase text-sm font-semibold tracking-wide">
                Service Features
              </span>

            </div>

            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900"
              style={{ fontFamily: "Sora" }}
            >
              What's included
            </h2>

          </div>

          {/* ITEMS */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            {s.items.map((it, i) => (
              <motion.div
                key={it}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-[24px] border border-violet-100 bg-white p-5 md:p-6 shadow-sm hover:shadow-lg transition"
              >

                <div className="flex items-start gap-4">

                  <div className="w-10 h-10 rounded-xl bg-violet-100 border border-violet-200 flex items-center justify-center shrink-0">

                    <CheckCircle2 className="w-5 h-5 text-violet-600" />

                  </div>

                  <div>

                    <div
                      className="text-base md:text-lg font-semibold text-slate-900"
                      style={{ fontFamily: "Sora" }}
                    >
                      {it}
                    </div>

                    <div className="text-slate-500 text-sm mt-2 leading-relaxed">
                      Delivered by experienced engineers using modern technologies.
                    </div>

                  </div>

                </div>

              </motion.div>
            ))}

          </div>

        </div>

      </section>
    </>
  );
}